/**
 * The session drawer's Autonomy section (Details tab): who decides this session's approvals,
 * its permission mode and where that comes from, what happens when nobody answers, session
 * control for its machine, and its "Allow for session" answers with Revoke. A running session
 * has no narrower scope than its grants, so the level changes for its project, in place.
 */
import { SlidersHorizontal, X } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import type { EffectiveSetting, Session, SessionAllow } from '@orgus/protocol';
import { resolveSettings } from '@orgus/policy';
import { Button, ErrorState, RelativeTime, Tooltip } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { toUiError } from '../../lib/api.ts';
import { useMachine, useOrgus, useProject } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { useApprovals } from '../approvals/live.ts';
import { useExpirySettings } from '../approvals/expiryModel.ts';
import { ControlSwitch } from '../control/ControlSwitch.tsx';
import { useControlState } from '../control/live.ts';
import { fetchLaunches, usePolicyV2 } from '../policy/live.ts';
import { openProjectAutonomy, revokeSessionAllow } from './api.ts';
import { allowText, approvalRoute, controlWaitText, expiryFact, permissionFact, routeFact, sessionAllowsFor } from './model.ts';

/** A labelled fact: the tiny label, the value, and one dim line on where it comes from. */
function Fact({ label, note, children, aside }: { label: string; note?: ReactNode; children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="group/fact flex min-w-0 flex-col gap-1">
      <dt className="text-caption text-fg-subtle">{label}</dt>
      <dd className="flex min-w-0 items-center gap-3">
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-ui text-fg">{children}</span>
          {note && <span className="truncate text-caption text-fg-subtle">{note}</span>}
        </span>
        {aside}
      </dd>
    </div>
  );
}

/** The permission mode this session was launched with, and its source, from its launch record (live hubs only). */
function useLaunchedMode(s: Session): Pick<EffectiveSetting, 'value' | 'source'> | null {
  const mode = useOrgus((st) => st.mode);
  const [found, setFound] = useState<Pick<EffectiveSetting, 'value' | 'source'> | null>(null);
  useEffect(() => {
    setFound(null);
    if (mode !== 'live' || s.origin !== 'orgus') return;
    let alive = true;
    fetchLaunches(s.id)
      .then((list) => {
        const l = list[list.length - 1];
        const m = l?.effective.settings.find((x) => x.key === 'permissionMode');
        if (alive && m) setFound({ value: m.value, source: m.source });
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [s.id, s.origin, mode]);
  return found;
}

function AllowRow({ a, canRevoke, onError }: { a: SessionAllow; canRevoke: boolean; onError: (e: UiError | null) => void }) {
  const [busy, setBusy] = useState(false);
  const revoke = async () => {
    setBusy(true);
    onError(null);
    try {
      await revokeSessionAllow(a);
    } catch (e) {
      onError(toUiError(e, { what: 'The allow was not taken back.', next: 'Reload the drawer and try again.' }));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="flex h-row min-w-0 items-center gap-3 rounded-control px-2 hover:bg-hover">
      <span className="min-w-0 flex-1 truncate font-mono text-caption text-fg" title={allowText(a)}>
        {allowText(a)}
      </span>
      <span className="hidden shrink-0 text-caption text-fg-subtle sm:inline">
        {a.by === 'you' ? 'You' : a.by}, <RelativeTime ts={a.at} />
      </span>
      <Tooltip content={canRevoke ? 'Its next matching request asks again' : 'Taking an allow back needs the operator role or above.'}>
        <span tabIndex={canRevoke ? -1 : 0} className="inline-flex shrink-0">
          <Button size="sm" variant="ghost" icon={<X />} loading={busy} disabled={!canRevoke} onClick={() => void revoke()} aria-label={`Revoke ${allowText(a)} for this session`}>
            Revoke
          </Button>
        </span>
      </Tooltip>
    </li>
  );
}

export function SessionAutonomy({ s }: { s: Session }) {
  const project = useProject(s.projectId);
  const machine = useMachine(s.machineId);
  const control = useControlState();
  const m = control?.machines[s.machineId];
  const { approvals } = useApprovals();
  const v = usePolicyV2();
  const ex = useExpirySettings();
  const launched = useLaunchedMode(s);
  const canRevoke = useCan('operator');
  const [error, setError] = useState<UiError | null>(null);

  const route = approvalRoute(s, m);
  const how = routeFact(route);
  const projectName = project?.name ?? null;
  const resolved = launched ?? resolveSettings(v.config, s.projectId).find((r) => r.key === 'permissionMode');
  const mode = permissionFact(s, resolved, projectName);
  const expiry = expiryFact(ex.settings, s.projectId, projectName);
  const allows = sessionAllowsFor(approvals?.sessionAllows, s);
  const machineName = machine?.name ?? 'its machine';

  return (
    <section aria-label="Autonomy" className="flex flex-col gap-3">
      <div className="flex min-w-0 items-center gap-2">
        <h3 className="shrink-0 text-caption font-medium text-fg-subtle">Autonomy</h3>
        {project ? (
          <Button size="sm" variant="ghost" icon={<SlidersHorizontal />} className="ml-auto min-w-0" onClick={() => openProjectAutonomy(project.id)}>
            <span className="truncate">Change for project {project.name}</span>
          </Button>
        ) : (
          <Link to="/settings/launch" className="ml-auto inline-flex h-control-sm items-center gap-2 text-caption text-fg-muted hover:text-fg">
            <SlidersHorizontal className="size-4 text-fg-subtle" aria-hidden />
            Change the defaults
          </Link>
        )}
      </div>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
        <Fact label="Approvals" note={how.note}>
          {how.value}
        </Fact>
        <Fact label="Permission mode" note={mode.note}>
          {mode.value}
        </Fact>
        {route !== 'watched' && (
          <Fact label="When nobody answers" note={route === 'launched' ? expiry.note : undefined}>
            {route === 'launched' ? expiry.value : controlWaitText(m?.waitSeconds ?? 120)}
          </Fact>
        )}
        <Fact
          label="Session control"
          note={m ? `Every session on ${machineName}` : 'Not reported by its machine'}
          // The value reads at rest; its switch shows on hover or focus (always on a touch screen).
          aside={
            m && machine ? (
              <span className="inline-flex shrink-0 transition-opacity duration-(--duration-fast) pointer-fine:opacity-0 pointer-fine:group-hover/fact:opacity-100 pointer-fine:group-focus-within/fact:opacity-100">
                <ControlSwitch machineId={s.machineId} name={machineName} online={machine.online} m={m} />
              </span>
            ) : undefined
          }
        >
          {m?.installed ? 'On' : 'Off'}
        </Fact>
      </dl>
      {allows.length > 0 && (
        <div className="flex flex-col gap-1">
          <h4 className="text-caption text-fg-subtle">Allowed for this session</h4>
          <ul className="-mx-2 flex flex-col" aria-label="Allowed for this session">
            {allows.map((a) => (
              <AllowRow key={`${a.tool}:${a.pattern}`} a={a} canRevoke={canRevoke} onError={setError} />
            ))}
          </ul>
        </div>
      )}
      {error && <ErrorState variant="inline" error={error} />}
    </section>
  );
}
